// The list of attributes down the left of the panel.
//
// Ranked by the best split the engine would actually accept, expressed as the
// share of the node's uncertainty it removes, rather than alphabetically. Two
// reasons for that scoring choice over the obvious alternatives: raw lift ranks
// a three lead bucket at 100% above everything real, and a spread measure like
// information value ranks attributes that have plenty of spread and no legal way
// to exploit it. Scoring by the best legal candidate means the top of this list
// cannot recommend something the engine will refuse to build.
import React from 'react';
import { AttributeStats, attributeScore } from './bucketStats';
import './nodeInsights.css';

export interface RankedAttribute {
  id: string;
  name: string;
  /**
   * One sentence saying what the field measures, when the dictionary carries it.
   *
   * Absent for a tenant's own computation, which nothing has written a description for.
   */
  description?: string;
  stats: AttributeStats;
  score: number;
}

export type RailSort = 'best' | 'name';

export function rankAttributes(
  entries: { id: string; name: string; stats: AttributeStats }[],
  sort: RailSort
): RankedAttribute[] {
  const ranked = entries.map((entry) => ({
    ...entry,
    score: attributeScore(entry.stats),
  }));
  return sort === 'name'
    ? ranked.sort((a, b) => a.name.localeCompare(b.name))
    : ranked.sort((a, b) => b.score - a.score || a.name.localeCompare(b.name));
}

export default function ComputationRail({
  attributes,
  selectedId,
  search,
  sort,
  onSelect,
  onSearch,
  onSort,
}: {
  attributes: RankedAttribute[];
  selectedId: string;
  search: string;
  sort: RailSort;
  onSelect: (id: string) => void;
  onSearch: (value: string) => void;
  onSort: (sort: RailSort) => void;
}): JSX.Element {
  const needle = search.trim().toLowerCase();
  // Matched on BOTH names. The rail shows the business label, but a tree node's own
  // condition is still written in the raw column ("maturity range"), so on one page the
  // same field goes by two names. Someone who has just read a node and wants the attribute
  // behind it types what the node said, and searching only the label finds nothing.
  const shown = needle
    ? attributes.filter(
        (a) =>
          a.name.toLowerCase().includes(needle) ||
          a.id.toLowerCase().replace(/_/g, ' ').includes(needle) ||
          a.id.toLowerCase().includes(needle)
      )
    : attributes;
  // Bars are relative to the strongest attribute on this node, so the rail
  // reads as a ranking. The absolute figure lives on the verdict banner.
  const top = Math.max(...attributes.map((a) => a.score), 0.0001);

  return (
    <div className="nins-rail">
      <div className="nins-rail-head">
        <input
          className="nins-search"
          type="search"
          value={search}
          placeholder="Search attributes"
          aria-label="Search attributes"
          onChange={(e) => onSearch(e.target.value)}
        />
        <div className="nins-sort">
          <button
            type="button"
            aria-pressed={sort === 'best'}
            onClick={() => onSort('best')}
          >
            Best split
          </button>
          <button
            type="button"
            aria-pressed={sort === 'name'}
            onClick={() => onSort('name')}
          >
            A to Z
          </button>
        </div>
      </div>
      {/* Says how many there are and that the list runs on, because a list
          that simply stops at the edge of its box does not look scrollable. */}
      <div className="nins-count">
        {shown.length} attribute{shown.length === 1 ? '' : 's'}
        {needle && ` matching ${search}`}
      </div>
      <div className="nins-list-wrap">
        <div className="nins-list">
          {shown.length === 0 && (
            <p className="nins-empty">Nothing matches {search}.</p>
          )}
          {/* `data-column` carries the raw column: the rail shows the business label
              while a tree node's own condition is written in the column, so one page
              calls a field two things. The row is too tight to show both, so it is
              queryable here and rendered properly in the panel head. */}
          {shown.map((attribute) => (
            <button
              type="button"
              key={attribute.id}
              className={`nins-item ${
                attribute.id === selectedId ? 'nins-item--on' : ''
              }`}
              onClick={() => onSelect(attribute.id)}
              title={
                attribute.score
                  ? `Best split here removes ${(attribute.score * 100).toFixed(
                      2
                    )}% of this node's uncertainty`
                  : attribute.stats.ineligible
                  ? `Not a field to split on: ${attribute.stats.ineligible}`
                  : attribute.stats.ineligibleHere
                  ? `Not this close to the root: ${attribute.stats.ineligibleHere}`
                  : 'No split on this attribute clears the minimums'
              }
              data-column={attribute.id}
            >
              <span className="nins-item-name">{attribute.name}</span>
              <span className="nins-score">
                <i style={{ width: `${(attribute.score / top) * 100}%` }} />
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
